import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import {
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  Alert,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Badge,
  Menu,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FolderSpecial as ProjectIcon,
  FolderOff as NoProjectIcon,
  MoreVert as MoreVertIcon,
  Cancel as CancelIcon,
  Replay as ReplayIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  BookmarkAdd as SaveToLibraryIcon,
  DriveFileMoveOutlined as MoveProjectIcon,
} from '@mui/icons-material';
import MoveObservationDialog from '../../components/MoveObservationDialog';
import { parseISO } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { websocketService } from '../../services/websocketService';
import DispatchStatusIndicator from '../../components/DispatchStatusIndicator';
import SaveToLibraryDialog from '../../components/library/SaveToLibraryDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import { isObservationEditable } from '../../utils/observationEditable';
import { canResubmitObservation } from '../../utils/observationActions';
import { filterGroupedObservations, countHiddenByObservatory } from '../../utils/observationFilter';
import { getAccessToken } from '../../services/authStorage';

const TERMINAL_OBS_STATES = ['completed', 'failed', 'cancelled', 'aborted', 'partially_completed'];

const ObservationList = ({ mode = 'user' }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateUserPreferences } = useAuth();
  const [groupedObservations, setGroupedObservations] = useState({});
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const isInitialLoad = useRef(true);
  const [inactiveObservatoryMessage, setInactiveObservatoryMessage] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [dispatchStatus, setDispatchStatus] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  const [observatories, setObservatories] = useState([]);
  const [selectedObservatory, setSelectedObservatory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [saveToLibraryObs, setSaveToLibraryObs] = useState(null);
  const [libraryProjects, setLibraryProjects] = useState([]);
  const [moveTarget, setMoveTarget] = useState(null); // {mode:'single'|'bulk', observation?, observationIds?}
  const [statusFilters, setStatusFilters] = useState({
    pending: true,
    assigned: true,
    in_progress: true,
    acquisition_complete: true,
    completed: true,
    completed_series: true,
    partially_completed: true,
    waiting_next: true,
    paused: true,
    suspended: true,
    failed: true,
    aborted: true,
    cancelled: true,
    expired: true,
  });

  // Get project filter from URL
  const projectFilter = searchParams.get('project_id');
  // Observatory filter from URL (e.g. Control Center → Observatories row
  // → Observations action passes ?assigned_to=<id> so we land scoped to
  // that one observatory instead of 'all').
  const observatoryFilter = searchParams.get('assigned_to');
  // ?scoped=1 (set by the Control Center floating-window launch) hides
  // the observatory tab bar entirely — the window is already dedicated
  // to one observatory and the bar is just visual noise.
  const scopedToObservatory = searchParams.get('scoped') === '1';

  // Sync URL-driven observatory selection to local state. We do this in
  // an effect rather than in the useState initialiser so that later
  // navigations to the same page with a different ?assigned_to also
  // flip the tab — initialisers only run on first mount.
  useEffect(() => {
    if (observatoryFilter && observatoryFilter !== selectedObservatory) {
      setSelectedObservatory(observatoryFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observatoryFilter]);

  // Determine if admin features should be shown - based on mode, not user role
  const isAdmin = mode === 'admin';
  const showUserOnly = mode === 'user';

  // Filter observations by selected observatory, status, and project URL param
  const filteredGroupedObservations = React.useMemo(
    () => filterGroupedObservations(groupedObservations, {
      selectedObservatory,
      statusFilters,
      projectFilter,
    }),
    [groupedObservations, selectedObservatory, statusFilters, projectFilter]
  );

  // How many status-visible observations the observatory filter is hiding.
  // When the page auto-selects a saved observatory, observations assigned to
  // other observatories vanish with no indication — this drives a banner so
  // the user can recover them with one click instead of thinking they're gone.
  const hiddenByObservatoryCount = React.useMemo(
    () => countHiddenByObservatory(groupedObservations, {
      selectedObservatory,
      statusFilters,
      projectFilter,
    }),
    [groupedObservations, selectedObservatory, statusFilters, projectFilter]
  );

  // Auto-expand project when filtering by project_id
  useEffect(() => {
    if (projectFilter && groupedObservations[projectFilter]) {
      setExpandedProjects(prev => ({ ...prev, [projectFilter]: true }));
    }
  }, [projectFilter, groupedObservations]);

  // Helper to clear project filter
  const clearProjectFilter = () => {
    setSearchParams({});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, []);

  // Update time remaining every second for active observations
  useEffect(() => {
    const hasActiveObservations = Object.values(groupedObservations).some(observations =>
      observations.some(obs => ['assigned', 'in_progress'].includes(obs.status))
    );

    if (hasActiveObservations) {
      const interval = setInterval(() => {
        // Force re-render to update time remaining displays
        setGroupedObservations(prev => ({ ...prev }));
      }, 1000); // Update every second

      return () => clearInterval(interval);
    }
  }, [groupedObservations]);


  useEffect(() => {
    // Set up WebSocket listener for observation updates
    const handleObservationUpdate = async (data) => {
      console.log('WebSocket observation update received:', data);

      const observationId = data.observation_id || data._id || data.id;

      try {
        // Fetch the updated observation
        const response = await apiService.getObservation(observationId);
        const updatedObservation = response.data;

        // Update the observation in state
        setGroupedObservations(prev => {
          const newGrouped = { ...prev };

          // Find and update the observation in its project group
          let found = false;
          Object.keys(newGrouped).forEach(projectId => {
            const index = newGrouped[projectId].findIndex(obs => obs._id === observationId);
            if (index !== -1) {
              // Preserve locally-tracked progress if it's ahead of the API response
              // (handles race between progress events and completion DB updates)
              const existing = newGrouped[projectId][index];
              const existingCaptured = existing.observation_summary?.files_captured || 0;
              const fetchedCaptured = updatedObservation.observation_summary?.files_captured || 0;
              if (existingCaptured > fetchedCaptured) {
                updatedObservation.observation_summary = {
                  ...(updatedObservation.observation_summary || {}),
                  files_captured: existingCaptured,
                  files_expected: existing.observation_summary?.files_expected || updatedObservation.observation_summary?.files_expected,
                  completion_percentage: existing.observation_summary?.completion_percentage || updatedObservation.observation_summary?.completion_percentage,
                };
              }
              newGrouped[projectId][index] = updatedObservation;
              found = true;
            }
          });

          // If not found in any group, add it (new observation or moved between projects)
          if (!found) {
            const projectId = updatedObservation.project_id?._id || updatedObservation.project_id || 'no-project';
            if (!newGrouped[projectId]) {
              newGrouped[projectId] = [];
            }
            newGrouped[projectId] = [updatedObservation, ...newGrouped[projectId]];
          }

          return newGrouped;
        });
      } catch (error) {
        console.error('Failed to fetch updated observation:', error);
      }
    };

    // Handler for newly created observations
    const handleObservationCreated = async (data) => {
      console.log('WebSocket observation_created received:', data);

      const observationId = data.observation_id || data._id || data.id;

      try {
        const response = await apiService.getObservation(observationId);
        const newObservation = response.data;

        // In user-only mode, skip observations not created by this user
        if (showUserOnly && user) {
          const userId = user._id || user.id;
          const creatorId = newObservation.created_by?._id || newObservation.created_by?.id;
          if (creatorId !== userId) return;
        }

        setGroupedObservations(prev => {
          const newGrouped = { ...prev };
          const projectId = newObservation.project_id?._id || newObservation.project_id || 'no-project';
          if (!newGrouped[projectId]) {
            newGrouped[projectId] = [];
          }
          // Avoid duplicates
          if (!newGrouped[projectId].some(obs => obs._id === newObservation._id)) {
            newGrouped[projectId] = [newObservation, ...newGrouped[projectId]];
          }
          return newGrouped;
        });

        // Refresh dispatch status if new observation is pending (debounced — #1309).
        if (newObservation.status === 'pending' || newObservation.status === 'waiting_next') {
          debouncedRefreshDispatch();
        }
      } catch (error) {
        console.error('Failed to fetch new observation:', error);
      }
    };

    // Handler for real-time progress updates (no API round-trip needed)
    const handleObservationProgress = (data) => {
      const observationId = data.observation_id;
      if (!observationId) return;

      setGroupedObservations(prev => {
        const newGrouped = { ...prev };
        let found = false;

        Object.keys(newGrouped).forEach(projectId => {
          const index = newGrouped[projectId].findIndex(obs => obs._id === observationId);
          if (index !== -1) {
            found = true;
            const obs = { ...newGrouped[projectId][index] };
            // Update observation_summary with live progress data
            obs.observation_summary = {
              ...obs.observation_summary,
              files_captured: data.frames_captured,
              files_expected: data.frames_expected,
              completion_percentage: data.progress_percent ||
                (data.frames_expected > 0 ? Math.round((data.frames_captured / data.frames_expected) * 100) : 0),
            };
            newGrouped[projectId] = [...newGrouped[projectId]];
            newGrouped[projectId][index] = obs;
          }
        });

        return found ? newGrouped : prev;
      });
    };

    // Handler for deleted observations
    const handleObservationDeleted = (data) => {
      console.log('WebSocket observation_deleted received:', data);
      const observationId = data.observation_id || data._id || data.id;
      if (!observationId) return;

      setGroupedObservations(prev => {
        const newGrouped = { ...prev };
        Object.keys(newGrouped).forEach(projectId => {
          newGrouped[projectId] = newGrouped[projectId].filter(obs => obs._id !== observationId);
          if (newGrouped[projectId].length === 0) {
            delete newGrouped[projectId];
          }
        });
        return newGrouped;
      });
    };

    // Handler for project reassignment — refetch the observation so we
    // pick up the new populated project_id, then migrate the row from
    // its old project group to the new one.
    const handleObservationMoved = async (data) => {
      const observationId = data.observation_id || data._id || data.id;
      if (!observationId) return;
      try {
        const response = await apiService.getObservation(observationId);
        const updated = response.data;
        const newProjectId = updated.project_id?._id || updated.project_id || 'no-project';
        setGroupedObservations(prev => {
          const next = {};
          Object.keys(prev).forEach(projectId => {
            const filtered = prev[projectId].filter(obs => obs._id !== observationId);
            if (filtered.length > 0) next[projectId] = filtered;
          });
          if (!next[newProjectId]) next[newProjectId] = [];
          next[newProjectId] = [updated, ...next[newProjectId]];
          return next;
        });
        // Cache project metadata if we just learned about a new one.
        if (updated.project_id && typeof updated.project_id === 'object') {
          setProjects(prev => prev[newProjectId]
            ? prev
            : { ...prev, [newProjectId]: updated.project_id });
        }
      } catch (err) {
        console.error('Failed to refetch moved observation:', err);
      }
    };

    // Register the WebSocket listeners
    websocketService.on('observation_created', handleObservationCreated);
    websocketService.on('observation_updated', handleObservationUpdate);
    websocketService.on('observation_assigned', handleObservationUpdate);
    websocketService.on('observation_started', handleObservationUpdate);
    websocketService.on('observation_completed', handleObservationUpdate);
    websocketService.on('observation_failed', handleObservationUpdate);
    websocketService.on('observation_aborted', handleObservationUpdate);
    websocketService.on('observation_cancelled', handleObservationUpdate);
    websocketService.on('observation_expired', handleObservationUpdate);
    websocketService.on('observation_progress', handleObservationProgress);
    websocketService.on('observation_deleted', handleObservationDeleted);
    // #1309: ObservationList no longer refreshes on observation_log_entry. A log entry
    // changes no row field the status/progress handlers don't already deliver, and its
    // dispatch-status refresh was the scheduler firehose (one full per-observation
    // check-constraints fan-out per plugin log line). The log view (ObservationLog)
    // subscribes to log entries on its own. Dispatch status now refreshes on a 45s timer
    // + schedule-altering events (debounced) — see refreshDispatchStatus above.
    websocketService.on('observation_moved', handleObservationMoved);

    // Cleanup
    return () => {
      websocketService.off('observation_created', handleObservationCreated);
      websocketService.off('observation_updated', handleObservationUpdate);
      websocketService.off('observation_assigned', handleObservationUpdate);
      websocketService.off('observation_started', handleObservationUpdate);
      websocketService.off('observation_completed', handleObservationUpdate);
      websocketService.off('observation_failed', handleObservationUpdate);
      websocketService.off('observation_aborted', handleObservationUpdate);
      websocketService.off('observation_cancelled', handleObservationUpdate);
      websocketService.off('observation_expired', handleObservationUpdate);
      websocketService.off('observation_progress', handleObservationProgress);
      websocketService.off('observation_deleted', handleObservationDeleted);
      websocketService.off('observation_moved', handleObservationMoved);
    };
  }, [showUserOnly, user]);

  // Refresh dispatch status for pending and waiting_next observations.
  // #1309: memoized with a stable identity (useCallback []) so the 1s "time remaining"
  // re-render loop can't tear down the 45s interval below, and generation-guarded so a
  // slow response can't overwrite a newer one. Reads only apiService + setDispatchStatus.
  const dispatchGenRef = useRef(0);
  const refreshDispatchStatus = useCallback(async () => {
    const gen = ++dispatchGenRef.current;
    try {
      const [pendingResponse, waitingResponse] = await Promise.all([
        apiService.getDispatchStatus(null, 'pending').catch(() => ({ success: false, data: { observations: [] } })),
        apiService.getDispatchStatus(null, 'waiting_next').catch(() => ({ success: false, data: { observations: [] } }))
      ]);

      const dispatchMap = {};
      if (pendingResponse.success && pendingResponse.data) {
        pendingResponse.data.observations.forEach(obs => { dispatchMap[obs.id] = obs; });
      }
      if (waitingResponse.success && waitingResponse.data) {
        waitingResponse.data.observations.forEach(obs => { dispatchMap[obs.id] = obs; });
      }
      if (gen !== dispatchGenRef.current) return; // a newer refresh already superseded this one
      setDispatchStatus(dispatchMap);
    } catch (err) {
      console.error('Failed to refresh dispatch status:', err);
    }
  }, []);

  // #1309: debounced entry point for event-driven refreshes (coalesces bursts).
  const [debouncedRefreshDispatch] = useDebouncedCallback(refreshDispatchStatus, { delay: 2000, maxWait: 5000 });

  // #1309: periodic refresh so time-drifting constraints (altitude near the horizon)
  // update without the per-log-entry firehose (there was no periodic before). 45s is
  // immaterial staleness on a status chip; refreshDispatchStatus is stable so this runs once.
  useEffect(() => {
    const id = setInterval(() => { refreshDispatchStatus(); }, 45000);
    return () => clearInterval(id);
  }, [refreshDispatchStatus]);

  // Debounced target name search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const response = await apiService.searchObservations({ q: searchQuery, limit: 100 });
        if (response.success) {
          setSearchResults(response.data.observations);
        }
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const loadData = async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) setLoading(true);
      setError(null);
      
      // Load observations, projects, and observatories
      const obsParams = { limit: 1000 };
      if (showUserOnly && user) {
        obsParams.created_by = user._id || user.id;
      }
      const [observationsResponse, projectsResponse, observatoriesResponse] = await Promise.all([
        apiService.getObservations(obsParams),
        apiService.getAllProjects(),
        apiService.getObservatories()
      ]);

      // Extract observations array from response
      let allObservations = observationsResponse.data.observations || observationsResponse.data;

      // Filter for user's observations if in user mode
      if (showUserOnly && user) {
        const userId = user._id || user.id;
        allObservations = allObservations.filter(obs => {
          const creatorId = obs.created_by?._id || obs.created_by?.id;
          return creatorId === userId;
        });
      }

      // Store observatories for tabs
      let observatoriesList = observatoriesResponse.data?.observatories || [];

      // For user mode, filter to only observatories in their observations
      if (showUserOnly && user) {
        const userObsIds = new Set();
        allObservations.forEach(obs => {
          if (obs.assigned_to?._id) userObsIds.add(obs.assigned_to._id);
          if (obs.assigned_to?.id) userObsIds.add(obs.assigned_to.id);
        });
        observatoriesList = observatoriesList.filter(obs =>
          userObsIds.has(obs._id) || userObsIds.has(obs.id)
        );
      }

      setObservatories(observatoriesList);

      // Pre-select saved observatory preference
      const savedObsId = user?.preferences?.last_observatory_id;
      if (savedObsId && observatoriesList.find(o => o._id === savedObsId)) {
        setSelectedObservatory(savedObsId);
      }

      // Create project lookup map
      const projectMap = {};
      // getAllProjects resolves to a flat array (walks every page).
      if (Array.isArray(projectsResponse)) {
        projectsResponse.forEach(project => {
          projectMap[project._id] = project;
        });
      }
      setProjects(projectMap);
      
      // Group observations by project. project_id may arrive populated (object
      // with _id/name/code/...) or as a raw ObjectId string depending on the
      // populate path the route takes — extract the _id defensively, matching
      // the pattern used in the WebSocket update handlers above.
      const grouped = {};
      allObservations.forEach(obs => {
        const projectId = obs.project_id?._id || obs.project_id || 'no-project';
        if (!grouped[projectId]) {
          grouped[projectId] = [];
        }
        grouped[projectId].push(obs);
      });

      setGroupedObservations(grouped);

      // Fetch dispatch status for pending and waiting_next observations
      await refreshDispatchStatus();

      // Auto-expand projects with active observations only on initial load
      if (isInitialLoad.current) {
        const expanded = {};
        Object.keys(grouped).forEach(projectId => {
          const hasActive = grouped[projectId].some(obs =>
            ['in_progress', 'assigned', 'pending'].includes(obs.status)
          );
          if (hasActive) {
            expanded[projectId] = true;
          }
        });
        setExpandedProjects(expanded);
        isInitialLoad.current = false;
      }
      
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load observations');
    } finally {
      setLoading(false);
    }
  };

  const [deleteDialog, setDeleteDialog] = useState({ open: false, mode: 'single', observationId: null, ids: [], deleteFiles: false });

  // Open the delete dialog; the actual delete (and whether to also delete images) happens on confirm.
  const handleDeleteObservation = (observationId) => {
    setDeleteDialog({ open: true, mode: 'single', observationId, ids: [], deleteFiles: false });
  };

  const confirmDeleteObservation = async () => {
    const observationId = deleteDialog.observationId;
    const deleteFiles = deleteDialog.deleteFiles;
    setDeleteDialog(d => ({ ...d, open: false }));
    if (!observationId) return;

    try {
      const result = await apiService.deleteObservation(observationId, deleteFiles);
      console.log('Delete result:', result);
      
      // Show success message
      setError(null); // Clear any existing errors
      
      // Remove the observation from state immediately for better UX
      setGroupedObservations(prev => {
        const newGrouped = { ...prev };
        Object.keys(newGrouped).forEach(projectId => {
          newGrouped[projectId] = newGrouped[projectId].filter(obs => obs._id !== observationId);
          // Remove empty project groups
          if (newGrouped[projectId].length === 0) {
            delete newGrouped[projectId];
          }
        });
        return newGrouped;
      });
      
      // Reload data to ensure consistency
      setTimeout(() => loadData(false), 500);
    } catch (error) {
      console.error('Failed to delete observation:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        apiDetails: error.apiDetails
      });
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData?.error?.code === 'INVALID_ID') {
          setError('Invalid observation ID format. Please refresh the page and try again.');
        } else {
          setError(`Cannot delete: ${errorData?.error?.message || errorData?.message || 'Invalid request'}`);
        }
      } else if (error.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        // Optionally redirect to login
        setTimeout(() => window.location.href = '/login', 2000);
      } else if (error.response?.status === 403) {
        setError('You do not have permission to delete this observation.');
      } else {
        setError(`Failed to delete observation: ${error.response?.data?.error?.message || error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleCancelObservation = async (observationId) => {
    setConfirm({
      title: 'Cancel observation?',
      message: 'Are you sure you want to cancel this observation?',
      confirmLabel: 'Cancel observation',
      confirmColor: 'error',
      confirmIcon: <CancelIcon />,
      icon: <CancelIcon color="error" />,
      onConfirm: async () => {
        try {
          // Use cancelObservation which calls the /cancel endpoint and sends abort to plugin
          await apiService.cancelObservation(observationId, 'Cancelled by user');
          loadData(false);
        } catch (error) {
          console.error('Failed to cancel observation:', error);
          setError('Failed to cancel observation');
          throw error;
        }
      },
    });
  };

  const handleResubmitObservation = async (observation) => {
    // Check if assigned observatory is inactive before showing confirmation
    const observatory = observation.assigned_observatory || observation.assigned_to;
    if (observatory?.status === 'inactive') {
      setInactiveObservatoryMessage(`Observatory "${observatory.name}" has been deregistered. Please create a new observation and assign it to an active observatory.`);
      return;
    }

    const observationId = observation._id;

    setConfirm({
      title: 'Resubmit observation?',
      message: 'Are you sure you want to resubmit this observation? This will create a new observation with the same parameters.',
      confirmLabel: 'Resubmit',
      confirmColor: 'primary',
      confirmIcon: <ReplayIcon />,
      icon: <ReplayIcon color="primary" />,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/v1/observations/${observationId}/resubmit`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${getAccessToken()}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error?.message || 'Failed to resubmit observation');
          }

          // Reload the data to show the new observation
          loadData(false);
        } catch (error) {
          console.error('Failed to resubmit observation:', error);
          setError(error.message || 'Failed to resubmit observation');
          throw error;
        }
      },
    });
  };

  const handleSaveToLibrary = async ({ observation_id, project_id, save_target, save_template, template_name }) => {
    try {
      if (save_target) {
        await apiService.saveTargetsFromObservation(observation_id, { project_id });
      }
      if (save_template) {
        await apiService.saveTemplateFromObservation(observation_id, { project_id, name: template_name });
      }
    } catch (err) {
      throw new Error(err.response?.data?.error?.message || 'Failed to save to library');
    }
  };

  const openSaveToLibrary = async (observation) => {
    // Load projects for the dialog if not already loaded
    if (libraryProjects.length === 0) {
      try {
        const res = await apiService.getAllProjects();
        setLibraryProjects(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    }
    setSaveToLibraryObs(observation);
  };

  const handleActionMenuOpen = (event, observation) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedObservation(observation);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedObservation(null);
  };

  const handleAccordionChange = (projectId) => (event, isExpanded) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: isExpanded
    }));
  };

  // Get flat list of all observations for index-based selection
  const getAllObservations = () => {
    const allObs = [];
    Object.values(groupedObservations).forEach(observations => {
      observations.forEach(obs => allObs.push(obs));
    });
    return allObs;
  };

  const handleRowClick = (observation, event) => {
    // Don't handle selection if clicking on action buttons
    if (event.target.closest('button') || event.target.closest('.MuiIconButton-root')) {
      return;
    }

    const allObs = getAllObservations();
    const currentIndex = allObs.findIndex(obs => obs._id === observation._id);

    if (event.shiftKey && lastClickedIndex !== null) {
      // Range selection
      const start = Math.min(lastClickedIndex, currentIndex);
      const end = Math.max(lastClickedIndex, currentIndex);
      const newSelection = new Set(selectedRows);
      for (let i = start; i <= end; i++) {
        newSelection.add(allObs[i]._id);
      }
      setSelectedRows(newSelection);
    } else if (event.ctrlKey || event.metaKey) {
      // Toggle individual selection
      const newSelection = new Set(selectedRows);
      if (newSelection.has(observation._id)) {
        newSelection.delete(observation._id);
      } else {
        newSelection.add(observation._id);
      }
      setSelectedRows(newSelection);
      setLastClickedIndex(currentIndex);
    } else {
      // Single selection (clear others)
      setSelectedRows(new Set([observation._id]));
      setLastClickedIndex(currentIndex);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setDeleteDialog({ open: true, mode: 'bulk', observationId: null, ids: Array.from(selectedRows), deleteFiles: false });
  };

  const confirmBulkDelete = async () => {
    const selectedIds = deleteDialog.ids;
    const deleteFiles = deleteDialog.deleteFiles;
    setDeleteDialog(d => ({ ...d, open: false }));
    if (!selectedIds || selectedIds.length === 0) return;

    try {
      setLoading(true);

      const result = await apiService.bulkDeleteObservations(selectedIds, deleteFiles);
      console.log('Bulk delete result:', result);

      if (result.success) {
        const { deleted, failed, errors } = result.data;

        if (failed > 0) {
          setError(`Deleted ${deleted} observation${deleted !== 1 ? 's' : ''}, ${failed} failed: ${errors.map(e => e.error).join(', ')}`);
        } else {
          setError(null);
        }

        // Clear selection
        setSelectedRows(new Set());
        setLastClickedIndex(null);

        // Reload data (expanded state preserved - only auto-expanded on initial load)
        await loadData(false);
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      console.error('Error details:', error.response?.data);
      setError(`Failed to delete observations: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedRows(new Set());
    setLastClickedIndex(null);
  };

  // Human-readable status label (e.g. 'partially_completed' -> 'Partially Completed').
  const formatStatusLabel = (status) =>
    String(status || '')
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || status;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'completed_series':
        return 'success';
      case 'partially_completed':
        return 'secondary';
      case 'in_progress':
      case 'assigned':
      case 'acquisition_complete':
        return 'primary';
      case 'pending':
      case 'waiting_next':
        return 'info';
      case 'failed':
      case 'aborted':
      case 'expired':
        return 'error';
      case 'cancelled':
      case 'paused':
      case 'suspended':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getProjectStatusCounts = (observations) => {
    const counts = {
      total: observations.length,
      completed: 0,
      in_progress: 0,
      pending: 0,
      failed: 0
    };
    
    observations.forEach(obs => {
      if (obs.status === 'completed') counts.completed++;
      else if (obs.status === 'in_progress' || obs.status === 'assigned') counts.in_progress++;
      else if (obs.status === 'pending') counts.pending++;
      else if (obs.status === 'failed') counts.failed++;
    });
    
    return counts;
  };

  const calculateTimeRemaining = (observation) => {
    // Only show time remaining for active observations
    if (!['assigned', 'in_progress'].includes(observation.status)) {
      return '-';
    }

    // For fixed-time observations, use the fixed end time
    const endTimeField = observation.observation_type === 'fixed_time'
      ? observation.fixed_time_end
      : observation.utc_end_time;

    if (!endTimeField) {
      return '-';
    }

    const now = new Date();
    const endTime = new Date(endTimeField);
    const remaining = endTime - now;

    if (remaining <= 0) {
      return 'Expired';
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatStartTime = (observation) => {
    // For executed observations, show the actual start time
    if (['in_progress', 'completed', 'completed_series', 'partially_completed', 'failed', 'aborted'].includes(observation.status) && observation.started_at) {
      try {
        const utcDate = parseISO(observation.started_at);
        const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = utcDate.getUTCDate().toString().padStart(2, '0');
        const hours = utcDate.getUTCHours().toString().padStart(2, '0');
        const minutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
        const seconds = utcDate.getUTCSeconds().toString().padStart(2, '0');
        return `${month}/${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return '-';
      }
    }

    // For fixed-time observations, show the fixed start time
    if (observation.observation_type === 'fixed_time' && observation.fixed_time_start) {
      try {
        const utcDate = parseISO(observation.fixed_time_start);
        const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = utcDate.getUTCDate().toString().padStart(2, '0');
        const hours = utcDate.getUTCHours().toString().padStart(2, '0');
        const minutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
        const seconds = utcDate.getUTCSeconds().toString().padStart(2, '0');
        return `${month}/${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return '-';
      }
    }

    // For other observations, show the calculated start time
    // Check utc_start_time first, then fall back to constraints.start_time
    const startTime = observation.utc_start_time || observation.constraints?.start_time;
    if (!startTime) {
      return '-';
    }

    // Show scheduled time for pending, assigned, in_progress, completed, failed
    if (!['pending', 'assigned', 'in_progress', 'completed', 'failed'].includes(observation.status)) {
      return '-';
    }

    try {
      // Parse the ISO string and format in UTC with date
      const utcDate = parseISO(startTime);
      const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = utcDate.getUTCDate().toString().padStart(2, '0');
      const hours = utcDate.getUTCHours().toString().padStart(2, '0');
      const minutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
      const seconds = utcDate.getUTCSeconds().toString().padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return '-';
    }
  };

  const formatEndTime = (observation) => {
    // For in_progress observations, show "-" since they haven't ended yet
    if (observation.status === 'in_progress') {
      return '-';
    }

    // For terminal observations, show the actual end time
    if (['completed', 'completed_series', 'partially_completed', 'failed', 'aborted', 'expired'].includes(observation.status) && observation.completed_at) {
      try {
        const utcDate = parseISO(observation.completed_at);
        const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = utcDate.getUTCDate().toString().padStart(2, '0');
        const hours = utcDate.getUTCHours().toString().padStart(2, '0');
        const minutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
        const seconds = utcDate.getUTCSeconds().toString().padStart(2, '0');
        return `${month}/${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return '-';
      }
    }

    // For fixed-time observations, show the fixed end time (hard deadline)
    if (observation.observation_type === 'fixed_time' && observation.fixed_time_end) {
      try {
        const utcDate = parseISO(observation.fixed_time_end);
        const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = utcDate.getUTCDate().toString().padStart(2, '0');
        const hours = utcDate.getUTCHours().toString().padStart(2, '0');
        const minutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
        const seconds = utcDate.getUTCSeconds().toString().padStart(2, '0');
        return `${month}/${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return '-';
      }
    }

    // For other observations, show the calculated end time
    // Check utc_end_time first, then fall back to constraints.end_time
    const endTime = observation.utc_end_time || observation.constraints?.end_time;
    if (!endTime) {
      return '-';
    }

    // Show scheduled time for pending, assigned, in_progress, completed, failed
    if (!['pending', 'assigned', 'in_progress', 'completed', 'failed'].includes(observation.status)) {
      return '-';
    }

    try {
      // Parse the ISO string and format in UTC with date
      const utcDate = parseISO(endTime);
      const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = utcDate.getUTCDate().toString().padStart(2, '0');
      const hours = utcDate.getUTCHours().toString().padStart(2, '0');
      const minutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
      const seconds = utcDate.getUTCSeconds().toString().padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return '-';
    }
  };

  const formatTimeUTC = (timeString) => {
    if (!timeString) return null;
    try {
      const date = new Date(timeString);
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const seconds = date.getUTCSeconds().toString().padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}:${seconds} UTC`;
    } catch (e) {
      return null;
    }
  };

  const calculateTotalExposureTime = (observation) => {
    // For completed/failed/aborted observations, show actual duration from start to end
    if (['completed', 'failed', 'aborted'].includes(observation.status) &&
        observation.started_at && observation.completed_at) {
      try {
        const startTime = new Date(observation.started_at).getTime();
        const endTime = new Date(observation.completed_at).getTime();
        const durationMs = endTime - startTime;
        const totalMinutes = Math.floor(durationMs / 60000);
        const totalSeconds = Math.floor((durationMs % 60000) / 1000);
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        if (hours > 0) {
          return `${hours}h ${mins}m ${totalSeconds}s`;
        }
        return `${mins}m ${totalSeconds}s`;
      } catch (e) {
        // Fall through to estimated duration
      }
    }

    // Use estimated_duration_minutes if available (works for all observation types)
    if (observation.estimated_duration_minutes) {
      const hours = Math.floor(observation.estimated_duration_minutes / 60);
      const mins = observation.estimated_duration_minutes % 60;
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    }

    // Fallback to calculating from exposure configs
    let totalSeconds = 0;
    if (observation.exposure_plan?.total_exposure_time_seconds) {
      totalSeconds = observation.exposure_plan.total_exposure_time_seconds;
    } else if (observation.targets?.[0]?.exposure_configs?.length > 0) {
      observation.targets[0].exposure_configs.forEach(config => {
        totalSeconds += config.exposure_time * config.exposure_count;
      });
    } else if (observation.observation_summary?.total_exposure_time_seconds) {
      totalSeconds = observation.observation_summary.total_exposure_time_seconds;
    }

    if (totalSeconds > 0) {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    }

    return '-';
  };

  const getProgressInfo = (observation) => {
    if (observation.status === 'completed') {
      if (observation.observation_summary && observation.observation_summary.files_captured !== undefined) {
        const captured = observation.observation_summary.files_captured || 0;
        const expected = observation.observation_summary.files_expected || observation.expected_file_count || 0;
        const percentage = Math.round(observation.observation_summary.completion_percentage) ||
                          (expected > 0 ? Math.round((captured / expected) * 100) : 100);
        return {
          text: `${captured}/${expected}`,
          percentage,
          hasProgress: true
        };
      }
      
      if (observation.captured_file_count !== undefined && observation.expected_file_count !== undefined) {
        const percentage = observation.expected_file_count > 0 
          ? Math.round((observation.captured_file_count / observation.expected_file_count) * 100) 
          : 100;
        return {
          text: `${observation.captured_file_count}/${observation.expected_file_count}`,
          percentage,
          hasProgress: true
        };
      }
      
      if (observation.files && observation.files.length > 0) {
        let expected = 0;
        if (observation.targets?.[0]?.exposure_configs) {
          observation.targets[0].exposure_configs.forEach(config => {
            expected += config.exposure_count || 0;
          });
        }
        
        const percentage = expected > 0 ? Math.round((observation.files.length / expected) * 100) : 100;
        return {
          text: `${observation.files.length}/${expected || '?'}`,
          percentage,
          hasProgress: true
        };
      }
      
      return { text: 'Complete', percentage: 100, hasProgress: false };
    }

    if (observation.status === 'failed') {
      // For failed observations, show progress if any files were captured
      if (observation.observation_summary && observation.observation_summary.files_captured !== undefined) {
        const captured = observation.observation_summary.files_captured || 0;
        const expected = observation.observation_summary.files_expected || observation.expected_file_count || 0;
        if (captured > 0 || expected > 0) {
          const percentage = expected > 0 ? Math.round((captured / expected) * 100) : 0;
          return {
            text: `${captured}/${expected}`,
            percentage,
            hasProgress: true
          };
        }
      }

      if (observation.exposure_tracking) {
        const captured = observation.exposure_tracking.total_exposures_completed || 0;
        const expected = observation.exposure_tracking.total_exposures_expected || observation.expected_file_count || 0;
        if (captured > 0 || expected > 0) {
          const percentage = expected > 0 ? Math.round((captured / expected) * 100) : 0;
          return {
            text: `${captured}/${expected}`,
            percentage,
            hasProgress: true
          };
        }
      }

      if (observation.files && observation.files.length > 0) {
        let expected = 0;
        if (observation.targets?.[0]?.exposure_configs) {
          observation.targets[0].exposure_configs.forEach(config => {
            expected += config.exposure_count || 0;
          });
        }

        const percentage = expected > 0 ? Math.round((observation.files.length / expected) * 100) : 0;
        return {
          text: `${observation.files.length}/${expected || '?'}`,
          percentage,
          hasProgress: true
        };
      }

      // No files captured, just show "Failed"
      return { text: 'Failed', percentage: 0, hasProgress: false };
    }

    if (observation.status === 'partially_completed') {
      // Wind-down: show captured/total from the recorded partial completion, falling back
      // to exposure_tracking / captured_file_count.
      const pc = observation.partial_completion || {};
      const captured = pc.completed_exposures
        ?? observation.exposure_tracking?.total_exposures_completed
        ?? observation.captured_file_count ?? 0;
      const expected = pc.total_exposures
        ?? observation.exposure_tracking?.total_exposures_expected
        ?? observation.expected_file_count ?? 0;
      const percentage = pc.completion_percentage
        ?? (expected > 0 ? Math.round((captured / expected) * 100) : 0);
      return { text: `${captured}/${expected}`, percentage, hasProgress: true };
    }

    if (observation.status === 'in_progress' || observation.status === 'assigned') {
      // Try observation_summary first (used during completion)
      if (observation.observation_summary) {
        const captured = observation.observation_summary.files_captured || 0;
        const expected = observation.observation_summary.files_expected || observation.expected_file_count || 0;
        const percentage = expected > 0 ? Math.round((captured / expected) * 100) : 0;
        return {
          text: `${captured}/${expected}`,
          percentage,
          hasProgress: true
        };
      }

      // Fall back to exposure_tracking (real-time data during execution)
      if (observation.exposure_tracking) {
        const captured = observation.exposure_tracking.total_exposures_completed || 0;
        const expected = observation.exposure_tracking.total_exposures_expected || observation.expected_file_count || 0;
        const percentage = expected > 0 ? Math.round((captured / expected) * 100) : 0;
        return {
          text: `${captured}/${expected}`,
          percentage,
          hasProgress: true
        };
      }

      return { text: 'In Progress', percentage: 0, hasProgress: false };
    }
    
    return { text: '-', percentage: 0, hasProgress: false };
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
          <Button onClick={loadData} sx={{ ml: 2 }}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  const totalObservationCount = Object.values(groupedObservations).reduce((acc, obs) => acc + obs.length, 0);

  // Get observation counts per observatory for tab badges
  const getObservatoryCount = (observatoryId) => {
    let count = 0;
    Object.values(groupedObservations).forEach(observations => {
      observations.forEach(obs => {
        if (obs.assigned_to?._id === observatoryId || obs.assigned_to?.id === observatoryId) {
          count++;
        }
      });
    });
    return count;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">
            {isAdmin ? 'All Observations' : 'My Observations'}
          </Typography>
          {projectFilter && projects[projectFilter] && (
            <Chip
              label={`Project: ${projects[projectFilter].name}`}
              onDelete={clearProjectFilter}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedRows.size > 0 && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MoveProjectIcon />}
                onClick={() => setMoveTarget({ mode: 'bulk', observationIds: Array.from(selectedRows) })}
              >
                Move to Project ({selectedRows.size})
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedRows.size})
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={clearSelection}
              >
                Clear
              </Button>
            </>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Target search bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by target name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 350 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {searchLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
      </Box>

      {/* Search results */}
      {searchResults !== null ? (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Target</TableCell>
                  <TableCell>Observation</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Observatory</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((obs) => (
                  <TableRow
                    key={obs._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/observations/${obs._id}`, { state: { from: isAdmin ? 'admin' : 'user' } })}
                  >
                    <TableCell>{obs.targets?.[0]?.name || 'Unknown'}</TableCell>
                    <TableCell>{obs.name}</TableCell>
                    <TableCell>
                      <Chip label={formatStatusLabel(obs.status)} size="small" color={getStatusColor(obs.status)} />
                    </TableCell>
                    <TableCell sx={{ color: obs.assigned_to?.status === 'inactive' ? 'error.main' : undefined }}>
                      {obs.assigned_to?.name || 'Unassigned'}
                    </TableCell>
                    <TableCell>{obs.created_at ? new Date(obs.created_at).toLocaleDateString() : ''}</TableCell>
                  </TableRow>
                ))}
                {searchResults.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No observations found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
      <>

      {/* Observatory filter tabs */}
      {observatories.length > 0 && !scopedToObservatory && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedObservatory}
            onChange={(e, newValue) => { setSelectedObservatory(newValue); if (newValue !== 'all') updateUserPreferences({ last_observatory_id: newValue }); }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  All
                  <Chip label={totalObservationCount} size="small" />
                </Box>
              }
              value="all"
            />
            {observatories.map(obs => (
              <Tab
                key={obs._id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {obs.code || obs.name}
                    <Chip label={getObservatoryCount(obs._id)} size="small" />
                  </Box>
                }
                value={obs._id}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Observatory-filter hint: the page may auto-select a saved observatory,
          which silently drops observations assigned elsewhere. Surface that so
          the user can recover them instead of assuming they're gone. */}
      {hiddenByObservatoryCount > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setSelectedObservatory('all')}>
              Show all observatories
            </Button>
          }
        >
          {(() => {
            const selected = observatories.find(o => o._id === selectedObservatory);
            const label = selected ? (selected.name || selected.code) : 'this observatory';
            return `Showing only ${label}. ${hiddenByObservatoryCount} observation${hiddenByObservatoryCount === 1 ? '' : 's'} on other observatories ${hiddenByObservatoryCount === 1 ? 'is' : 'are'} hidden.`;
          })()}
        </Alert>
      )}

      {/* Status filter checkboxes */}
      <Box sx={{ mb: 2 }}>
        <FormGroup
          row
          sx={{
            columnGap: 0.5,
            rowGap: 0.5,
            // Drop MUI's default ~16px right margin on each label so the
            // checkbox+label pairs sit close together.
            '& .MuiFormControlLabel-root': { marginLeft: 0, marginRight: 0 },
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.pending}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, pending: e.target.checked }))}
              />
            }
            label="Pending"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.assigned}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, assigned: e.target.checked }))}
              />
            }
            label="Assigned"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.in_progress}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, in_progress: e.target.checked }))}
              />
            }
            label="In Progress"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.completed}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, completed: e.target.checked }))}
              />
            }
            label="Completed"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.partially_completed}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, partially_completed: e.target.checked }))}
              />
            }
            label="Partially Completed"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.completed_series}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, completed_series: e.target.checked }))}
              />
            }
            label="Completed Series"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.waiting_next}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, waiting_next: e.target.checked }))}
              />
            }
            label="Waiting Next"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.failed}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, failed: e.target.checked }))}
              />
            }
            label="Failed"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.aborted}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, aborted: e.target.checked }))}
              />
            }
            label="Aborted"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.cancelled}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, cancelled: e.target.checked }))}
              />
            }
            label="Cancelled"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={statusFilters.expired}
                onChange={(e) => setStatusFilters(prev => ({ ...prev, expired: e.target.checked }))}
              />
            }
            label="Expired"
          />
        </FormGroup>
      </Box>

      {Object.keys(filteredGroupedObservations).length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No observations found. Create your first observation to get started.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {Object.entries(filteredGroupedObservations).map(([projectId, observations]) => {
            const project = projects[projectId];
            const statusCounts = getProjectStatusCounts(observations);
            
            return (
              <Accordion 
                key={projectId}
                expanded={expandedProjects[projectId] || false}
                onChange={handleAccordionChange(projectId)}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  onClick={(e) => {
                    // Prevent accordion toggle when clicking delete button
                    if (e.target.closest('.delete-all-btn')) {
                      e.stopPropagation();
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                    {projectId === 'no-project' ? (
                      <NoProjectIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    ) : (
                      <ProjectIcon sx={{ mr: 2, color: 'primary.main' }} />
                    )}
                    <Typography sx={{ flexGrow: 1 }}>
                      {project ? project.name : 'No Project Assigned'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Badge badgeContent={statusCounts.completed} color="success" max={99}>
                        <Chip size="small" label="Completed" />
                      </Badge>
                      <Badge badgeContent={statusCounts.in_progress} color="primary" max={99}>
                        <Chip size="small" label="Active" />
                      </Badge>
                      <Badge badgeContent={statusCounts.pending} color="default" max={99}>
                        <Chip size="small" label="Pending" />
                      </Badge>
                      {statusCounts.failed > 0 && (
                        <Badge badgeContent={statusCounts.failed} color="error" max={99}>
                          <Chip size="small" label="Failed" />
                        </Badge>
                      )}
                    </Stack>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Target</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Cadence</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Observatory</TableCell>
                          <TableCell>Progress</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Start Time</TableCell>
                          <TableCell>End Time</TableCell>
                          <TableCell>Time Remaining</TableCell>
                          {isAdmin && <TableCell>Created By</TableCell>}
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {observations.map((observation) => {
                          const progressInfo = getProgressInfo(observation);
                          const isSelected = selectedRows.has(observation._id);
                          return (
                            <TableRow
                              key={observation._id}
                              onClick={(e) => handleRowClick(observation, e)}
                              sx={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                backgroundColor: isSelected ? 'action.selected' : 'inherit',
                                '&:hover': {
                                  backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                                },
                              }}
                            >
                              <TableCell>{observation.name}</TableCell>
                              <TableCell>{observation.target_name || observation.targets?.[0]?.name || '-'}</TableCell>
                              <TableCell>
                                {observation.observation_type === 'time_based' ? (
                                  <Chip
                                    label="Time-based"
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    title={`Duration: ${observation.estimated_duration_minutes || 'N/A'} min`}
                                  />
                                ) : observation.observation_type === 'fixed_time' ? (
                                  <Chip
                                    label="Fixed"
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    title="Fixed-time observation"
                                  />
                                ) : observation.observation_type === 'monitoring' ? (
                                  <Chip
                                    label="Monitoring"
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    title="Monitoring observation"
                                  />
                                ) : (
                                  <Chip
                                    label="Flex"
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                    title="Flexible observation"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {observation.observation_type === 'monitoring' && observation.cadence_days ? (
                                  <Chip
                                    label={observation.cadence_max_observations
                                      ? `${observation.cadence_days}d (${observation.cadence_observations_completed || 0}/${observation.cadence_max_observations})`
                                      : `${observation.cadence_days}d`}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    title={`Every ${observation.cadence_days} day${observation.cadence_days !== 1 ? 's' : ''}. ${observation.cadence_observations_completed || 0} completed${observation.cadence_max_observations ? ` of ${observation.cadence_max_observations}` : ''}`}
                                  />
                                ) : observation.repetition_config?.enabled ? (
                                  <Chip
                                    label={observation.repetition_config.max_executions
                                      ? `${observation.repetition_config.interval_days}d${observation.repetition_config.interval_hours ? `+${observation.repetition_config.interval_hours}h` : ''} (${observation.series_stats?.total_completed || 0}/${observation.repetition_config.max_executions})`
                                      : `${observation.repetition_config.interval_days}d${observation.repetition_config.interval_hours ? `+${observation.repetition_config.interval_hours}h` : ''}`}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    title={`Every ${observation.repetition_config.interval_days} day${observation.repetition_config.interval_days !== 1 ? 's' : ''}${observation.repetition_config.interval_hours ? ` and ${observation.repetition_config.interval_hours} hour${observation.repetition_config.interval_hours !== 1 ? 's' : ''}` : ''}. ${observation.series_stats?.total_completed || 0} completed${observation.repetition_config.max_executions ? ` of ${observation.repetition_config.max_executions}` : ''}`}
                                  />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell>{observation.priority}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip
                                    label={
                                      observation.status === 'waiting_next' && observation.series_stats
                                        ? `Run ${observation.series_stats.total_completed + 1} of ${observation.series_stats.total_planned || '∞'}`
                                        : observation.status === 'completed_series'
                                        ? `Series Complete (${observation.series_stats?.total_completed || 0})`
                                        : observation.status === 'paused'
                                        ? `Paused (${observation.series_stats?.total_completed || 0} done)`
                                        : formatStatusLabel(observation.status)
                                    }
                                    color={getStatusColor(observation.status)}
                                    size="small"
                                    title={
                                      // Only show next run time if we have valid scheduler data
                                      // Don't fall back to stale database values as they may be incorrect
                                      dispatchStatus[observation._id]?.next_observable_time
                                        ? `Next run: ${formatTimeUTC(dispatchStatus[observation._id].next_observable_time)}`
                                        : observation.series_stats
                                        ? `${observation.series_stats.total_completed} completed, ${observation.series_stats.total_failed} failed, ${observation.series_stats.total_skipped} skipped`
                                        : observation.status === 'waiting_next'
                                        ? 'Waiting for next observation window - see timeline for schedule'
                                        : observation.status
                                    }
                                  />
                                  <DispatchStatusIndicator
                                    observationId={observation._id}
                                    status={observation.status}
                                    dispatchInfo={dispatchStatus[observation._id]}
                                    compactMode={true}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>
                                {observation.assigned_to?.code ? (
                                  <Chip
                                    label={observation.assigned_to.code}
                                    size="small"
                                    variant={observation.assigned_to.status === 'inactive' ? 'filled' : 'outlined'}
                                    color={observation.assigned_to.status === 'inactive' ? 'error' : 'default'}
                                    sx={{ fontFamily: 'monospace' }}
                                  />
                                ) : observation.assigned_to?.name ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.75rem',
                                      color: observation.assigned_to.status === 'inactive' ? 'error.main' : 'text.secondary',
                                    }}
                                  >
                                    {observation.assigned_to.name}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 100 }}>
                                  {progressInfo.hasProgress ? (
                                    <>
                                      <Box sx={{ width: '100%', mr: 1 }}>
                                        <LinearProgress 
                                          variant="determinate" 
                                          value={progressInfo.percentage} 
                                        />
                                      </Box>
                                      <Box sx={{ minWidth: 35 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          {progressInfo.text}
                                        </Typography>
                                      </Box>
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      {progressInfo.text}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>{calculateTotalExposureTime(observation)}</TableCell>
                              <TableCell>{formatStartTime(observation)}</TableCell>
                              <TableCell>{formatEndTime(observation)}</TableCell>
                              <TableCell>
                                {['assigned', 'in_progress'].includes(observation.status) ? (
                                  <Typography
                                    variant="body2"
                                    color={calculateTimeRemaining(observation) === 'Expired' ? 'error' : 'text.secondary'}
                                  >
                                    {calculateTimeRemaining(observation)}
                                  </Typography>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              {isAdmin && (
                                <TableCell>
                                  {observation.created_by?.username || observation.created_by?.email || '-'}
                                </TableCell>
                              )}
                              <TableCell align="right">
                                <Tooltip title="Observation Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/observations/${observation._id}`, { state: { from: isAdmin ? 'admin' : 'user' } })}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                </Tooltip>
                                {isAdmin ? (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleActionMenuOpen(e, observation)}
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  </>
                                ) : (
                                  <>
                                    {isObservationEditable(observation.status) && (
                                      <Tooltip title="Edit">
                                        <IconButton
                                          size="small"
                                          onClick={() => navigate(`/observations/${observation._id}/edit`)}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {canResubmitObservation(observation.status) && (
                                      <Tooltip title="Resubmit">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleResubmitObservation(observation)}
                                        >
                                          <ReplayIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {TERMINAL_OBS_STATES.includes(observation.status) && (
                                      <Tooltip title="Move to Project">
                                        <IconButton
                                          size="small"
                                          onClick={() => setMoveTarget({ mode: 'single', observation })}
                                        >
                                          <MoveProjectIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteObservation(observation._id)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Save to Library">
                                      <IconButton
                                        size="small"
                                        onClick={() => openSaveToLibrary(observation)}
                                      >
                                        <SaveToLibraryIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      {/* Admin action menu */}
      {isAdmin && (
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
        >
          {isObservationEditable(selectedObservation?.status) && (
            <MenuItem onClick={() => {
              navigate(`/observations/${selectedObservation._id}/edit`);
              handleActionMenuClose();
            }}>
              <EditIcon sx={{ mr: 1 }} /> Edit
            </MenuItem>
          )}
          {canResubmitObservation(selectedObservation?.status) && (
            <MenuItem onClick={() => {
              handleResubmitObservation(selectedObservation);
              handleActionMenuClose();
            }}>
              <ReplayIcon sx={{ mr: 1 }} /> Resubmit
            </MenuItem>
          )}
          {['pending', 'assigned', 'in_progress'].includes(selectedObservation?.status) && (
            <MenuItem onClick={() => {
              handleCancelObservation(selectedObservation._id);
              handleActionMenuClose();
            }}>
              <CancelIcon sx={{ mr: 1 }} /> Cancel
            </MenuItem>
          )}
          {TERMINAL_OBS_STATES.includes(selectedObservation?.status) && (
            <MenuItem onClick={() => {
              setMoveTarget({ mode: 'single', observation: selectedObservation });
              handleActionMenuClose();
            }}>
              <MoveProjectIcon sx={{ mr: 1 }} /> Move to Project
            </MenuItem>
          )}
          <MenuItem onClick={() => {
            openSaveToLibrary(selectedObservation);
            handleActionMenuClose();
          }}>
            <SaveToLibraryIcon sx={{ mr: 1 }} /> Save to Library
          </MenuItem>
          <MenuItem onClick={() => {
            handleDeleteObservation(selectedObservation._id);
            handleActionMenuClose();
          }}>
            <DeleteIcon sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>
      )}

      {/* Save to Library Dialog */}
      <SaveToLibraryDialog
        open={!!saveToLibraryObs}
        onClose={() => setSaveToLibraryObs(null)}
        observation={saveToLibraryObs}
        projects={libraryProjects}
        onSave={handleSaveToLibrary}
      />

      {/* Move to Project Dialog */}
      <MoveObservationDialog
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        observation={moveTarget?.mode === 'single' ? moveTarget.observation : null}
        observationIds={moveTarget?.mode === 'bulk' ? moveTarget.observationIds : null}
        onMoved={({ moved }) => {
          // Successful single-mode moves close the dialog itself; bulk
          // mode closes only when there were no failures. Either way
          // clear bulk selection for the moved ids.
          if (moved && moved.length > 0) {
            setSelectedRows(prev => {
              const next = new Set(prev);
              moved.forEach(id => next.delete(id));
              return next;
            });
          }
        }}
      />

      {/* Delete observation(s) — optionally also delete the associated image files */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          {deleteDialog.mode === 'bulk'
            ? `Delete ${deleteDialog.ids.length} observation${deleteDialog.ids.length !== 1 ? 's' : ''}?`
            : 'Delete observation?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This action cannot be undone.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteDialog.deleteFiles}
                onChange={(e) => setDeleteDialog(d => ({ ...d, deleteFiles: e.target.checked }))}
              />
            }
            label="Also delete the associated image files (frees storage; cannot be undone)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteDialog.mode === 'bulk' ? confirmBulkDelete : confirmDeleteObservation}>
            {deleteDialog.deleteFiles ? 'Delete + images' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    {/* Inactive Observatory Dialog */}
    <Dialog open={!!inactiveObservatoryMessage} onClose={() => setInactiveObservatoryMessage(null)}>
      <DialogTitle>Observatory Inactive</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 1 }}>
          {inactiveObservatoryMessage}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setInactiveObservatoryMessage(null)} variant="contained">OK</Button>
      </DialogActions>
    </Dialog>
    <ConfirmDialog
      open={Boolean(confirm)}
      title={confirm?.title}
      message={confirm?.message}
      confirmLabel={confirm?.confirmLabel}
      confirmColor={confirm?.confirmColor}
      confirmIcon={confirm?.confirmIcon}
      icon={confirm?.icon}
      onConfirm={confirm?.onConfirm}
      onClose={() => setConfirm(null)}
    />
    </Box>
  );
};

export default ObservationList;